import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, Navigate, useOutletContext } from 'react-router-dom';
import { getAnthropicUsage, type AnthropicUsageTotals } from '../services/apiService';

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
    isActive
      ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200'
      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
  }`;

type GlobalAdminOutletContext = {
  /** Call after a catalog-translate action completes so the spend badge reflects the new total. */
  refreshAnthropicUsage: () => void;
};

/** Read the refresh callback from within a /global-admin/* route (e.g. after a translate action). */
export const useGlobalAdminOutletContext = () => useOutletContext<GlobalAdminOutletContext>();

/**
 * Estimated tokens/cost spent by this app's Claude translation calls (self-tracked — Anthropic
 * has no "remaining balance" API, and Admin API keys require an organization account).
 */
const AnthropicSpendBadge: React.FC<{ usage: AnthropicUsageTotals | null; err: boolean }> = ({ usage, err }) => {
  if (err || !usage) return null;

  const totalTokens = usage.input_tokens + usage.output_tokens;

  return (
    <span
      className="text-sm text-gray-600 dark:text-gray-400"
      title="Estimated from this app's Claude translation calls only (Opus 5 pricing) — not your full Anthropic account balance or spend."
    >
      {totalTokens.toLocaleString()} tokens ≈{' '}
      <span className="font-medium text-gray-800 dark:text-gray-200">${usage.estimated_usd.toFixed(2)}</span>{' '}
      <span className="text-xs">(translation)</span>
    </span>
  );
};

/**
 * Master admin shell: tabs for standard tools and standard tasks.
 */
const GlobalAdminLayout: React.FC = () => {
  const [usage, setUsage] = useState<AnthropicUsageTotals | null>(null);
  const [err, setErr] = useState(false);

  const refreshAnthropicUsage = useCallback(() => {
    getAnthropicUsage()
      .then((u) => {
        setUsage(u);
        setErr(false);
      })
      .catch(() => setErr(true));
  }, []);

  useEffect(() => {
    refreshAnthropicUsage();
  }, [refreshAnthropicUsage]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      <div>
        <NavLink to="/home" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
          ← Back to app
        </NavLink>
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mt-2">Master admin</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Manage global defaults that sync to families (where applicable).
        </p>
      </div>

      <nav className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 dark:border-gray-700 pb-4">
        <div className="flex flex-wrap gap-2">
          <NavLink to="/global-admin/tools" className={linkClass} end>
            Standard tools
          </NavLink>
          <NavLink to="/global-admin/tasks" className={linkClass}>
            Standard tasks
          </NavLink>
          <NavLink to="/global-admin/rooms" className={linkClass}>
            Standard rooms
          </NavLink>
          <NavLink to="/global-admin/rewards" className={linkClass}>
            Standard rewards
          </NavLink>
          <NavLink to="/global-admin/beta-codes" className={linkClass}>
            Beta codes
          </NavLink>
        </div>
        <AnthropicSpendBadge usage={usage} err={err} />
      </nav>

      <Outlet context={{ refreshAnthropicUsage } satisfies GlobalAdminOutletContext} />
    </div>
  );
};

export default GlobalAdminLayout;

/** Used when routing to /global-admin exactly */
export const GlobalAdminIndexRedirect: React.FC = () => <Navigate to="/global-admin/tools" replace />;
